/**
 * Bannière colorée distincte par niveau (L1/L2/L3/M1/M2).
 * Permet de séparer visuellement les listes mélangeant plusieurs niveaux.
 *
 * Usage minimal :
 *   <NiveauBanner niveau="L1" nbItems={23} sublabel="4 classes" />
 *
 * Usage avec collapse :
 *   <NiveauBanner
 *     niveau="L1"
 *     nbItems={23}
 *     collapsed={collapsed.has('L1')}
 *     onToggle={() => toggle('L1')}
 *   />
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface NiveauStyle {
  bg: string;       // gradient pour la bannière
  border: string;   // border-l-4 sur le wrapper
  text: string;     // text color (sur fond clair)
  badge: string;    // small badge (bg + text)
}

const STYLES: Record<string, NiveauStyle> = {
  L1: { bg: 'bg-gradient-to-r from-blue-500 to-blue-600',         border: 'border-blue-500',    text: 'text-blue-700 dark:text-blue-300',     badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  L2: { bg: 'bg-gradient-to-r from-emerald-500 to-emerald-600',   border: 'border-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  L3: { bg: 'bg-gradient-to-r from-purple-500 to-purple-600',     border: 'border-purple-500',  text: 'text-purple-700 dark:text-purple-300', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' },
  M1: { bg: 'bg-gradient-to-r from-orange-500 to-orange-600',     border: 'border-orange-500',  text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' },
  M2: { bg: 'bg-gradient-to-r from-rose-500 to-rose-600',         border: 'border-rose-500',    text: 'text-rose-700 dark:text-rose-300',     badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
};

const FALLBACK: NiveauStyle = {
  bg: 'bg-gradient-to-r from-gray-500 to-gray-600',
  border: 'border-gray-400',
  text: 'text-gray-700',
  badge: 'bg-gray-100 text-gray-800',
};

/** Helper exporté : récupère le style pour un niveau (avec fallback). */
export function getNiveauStyle(niveau: string): NiveauStyle {
  return STYLES[niveau] || FALLBACK;
}

/** Tri canonique des niveaux : L1, L2, L3, M1, M2 puis le reste alphabétique. */
export function compareNiveaux(a: string, b: string): number {
  const ordre = ['L1', 'L2', 'L3', 'M1', 'M2'];
  const ia = ordre.indexOf(a);
  const ib = ordre.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return a.localeCompare(b);
}

interface Props {
  niveau: string;
  nbItems: number;
  itemLabel?: string;        // défaut « relevé »
  sublabel?: string;          // ex: « 4 classes »
  collapsed?: boolean;
  onToggle?: () => void;
  sticky?: boolean;           // défaut true
  rightContent?: React.ReactNode;
}

const NiveauBanner: React.FC<Props> = ({
  niveau,
  nbItems,
  itemLabel = 'relevé',
  sublabel,
  collapsed = false,
  onToggle,
  sticky = true,
  rightContent,
}) => {
  const ns = getNiveauStyle(niveau);
  const cls = `${sticky ? 'sticky top-0 z-[5]' : ''} flex items-center justify-between px-5 py-3 ${ns.bg} text-white shadow-md ${onToggle ? 'cursor-pointer hover:brightness-110' : ''} transition`;

  return (
    <div onClick={onToggle} className={cls}>
      <div className="flex items-center gap-3">
        {onToggle && (collapsed
          ? <ChevronRight size={20} className="text-white/90" />
          : <ChevronDown size={20} className="text-white/90" />)}
        <span className="text-2xl font-extrabold tracking-wider">{niveau}</span>
        <span className="text-xs font-medium text-white/90">
          {sublabel && <>{sublabel} · </>}
          {nbItems} {itemLabel}{nbItems > 1 ? 's' : ''}
        </span>
      </div>
      {rightContent ? rightContent : (
        <span className="text-xs px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm font-semibold">
          {nbItems}
        </span>
      )}
    </div>
  );
};

export default NiveauBanner;
